// src/Dashboard/pages/teacher/SuiviCompetitions/hooks/useSuiviCompetitionsDashboard.js

import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  getAllEvents,
  getAdherentsByEvent,
  getAdherentsCountByEvent,
  getAdherentResults,
} from "../../../../../services/eventServices";

import { getAllEnseignants } from "../../../../../services/adherentServices";

import {
  CATE_EVENTS,
  PAGE_SIZE,
  eventIsPast,
  getEventCategories,
} from "../utils/suiviCompetitionsUtils";

const DEFAULT_FILTERS = {
  search: "",
  dateFilter: "upcoming",
  categoryFilter: "",
  typeFilter: "",
  onlyWithRegistrations: false,
};

export function useSuiviCompetitionsDashboard() {
  const [events, setEvents] = useState([]);
  const [enseignants, setEnseignants] = useState([]);

  const [filters, setFiltersState] = useState(DEFAULT_FILTERS);
  const [loading, setLoading] = useState(true);

  const [visibleLimit, setVisibleLimit] = useState(PAGE_SIZE);

  const [countsMap, setCountsMap] = useState({});
  const [expandedIds, setExpandedIds] = useState(new Set());

  const [registrantsByEvent, setRegistrantsByEvent] = useState({});
  const [registrantsLoadingMap, setRegistrantsLoadingMap] = useState({});

  const [registerDrawer, setRegisterDrawer] = useState({
    open: false,
    event: null,
    registeredIds: new Set(),
  });

  const [unregisterDrawer, setUnregisterDrawer] = useState({
    open: false,
    event: null,
    adherents: [],
  });

  const [resultDrawer, setResultDrawer] = useState({
    open: false,
    event: null,
    adherent: null,
    results: null,
    loading: false,
  });

  const setFilters = useCallback((updater) => {
    setFiltersState((previous) => {
      const next =
        typeof updater === "function"
          ? updater(previous)
          : { ...previous, ...updater };

      return {
        ...DEFAULT_FILTERS,
        ...next,
      };
    });
  }, []);

  const resetFilters = useCallback(() => {
    setFiltersState(DEFAULT_FILTERS);
  }, []);

  const loadCounts = useCallback(async (eventList = []) => {
    const entries = await Promise.allSettled(
      eventList.map(async (event) => {
        const response = await getAdherentsCountByEvent(event.id);
        const count =
          typeof response === "number"
            ? response
            : Number(response?.count || 0);

        return [event.id, count];
      })
    );

    const nextMap = {};

    entries.forEach((entry) => {
      if (entry.status === "fulfilled") {
        const [eventId, count] = entry.value;
        nextMap[eventId] = count;
      }
    });

    setCountsMap(nextMap);
  }, []);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);

      const [eventsData, enseignantsData] = await Promise.all([
        getAllEvents(),
        getAllEnseignants(),
      ]);

      const safeEvents = Array.isArray(eventsData) ? eventsData : [];
      const safeEnseignants = Array.isArray(enseignantsData)
        ? enseignantsData
        : [];

      setEvents(safeEvents);
      setEnseignants(safeEnseignants);

      loadCounts(safeEvents);
    } catch (error) {
      console.error("Erreur chargement suivi compétitions :", error);
      toast.error("Erreur lors du chargement des compétitions.");
    } finally {
      setLoading(false);
    }
  }, [loadCounts]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    setVisibleLimit(PAGE_SIZE);
  }, [
    filters.search,
    filters.dateFilter,
    filters.categoryFilter,
    filters.typeFilter,
    filters.onlyWithRegistrations,
  ]);

  const refresh = useCallback(async () => {
    await loadData();
  }, [loadData]);

  const eventTypes = useMemo(() => {
    const set = new Set((events || []).map((event) => event?.type_event).filter(Boolean));
    return Array.from(set).sort((a, b) => a.localeCompare(b, "fr"));
  }, [events]);

  const filteredEvents = useMemo(() => {
    const query = String(filters.search || "").trim().toLowerCase();

    return (events || []).filter((event) => {
      const title = String(event?.titre || "").toLowerCase();
      const type = String(event?.type_event || "");
      const categories = getEventCategories(event);

      const isPast = eventIsPast(event);

      const matchesSearch =
        !query ||
        [event?.titre, event?.type_event, event?.level_event, event?.lieu_event]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(query);

      const matchesDate =
        filters.dateFilter === "all" ||
        (filters.dateFilter === "past" && isPast) ||
        (filters.dateFilter === "upcoming" && !isPast);

      const matchesCategory =
        !filters.categoryFilter || categories.includes(filters.categoryFilter);

      const matchesType = !filters.typeFilter || type === filters.typeFilter;

      const count = countsMap[event.id];
      const matchesRegistrations =
        !filters.onlyWithRegistrations ||
        (typeof count === "number" ? count > 0 : true);

      return (
        matchesSearch &&
        matchesDate &&
        matchesCategory &&
        matchesType &&
        matchesRegistrations &&
        title !== ""
      );
    });
  }, [events, filters, countsMap]);

  const visibleEvents = useMemo(
    () => filteredEvents.slice(0, visibleLimit),
    [filteredEvents, visibleLimit]
  );

  const hasMore = visibleEvents.length < filteredEvents.length;

  const loadMore = useCallback(() => {
    setVisibleLimit((current) => current + PAGE_SIZE);
  }, []);

  const refreshEventRegistrants = useCallback(
    async (eventId) => {
      if (!eventId) return;

      try {
        setRegistrantsLoadingMap((previous) => ({
          ...previous,
          [eventId]: true,
        }));

        const [list, countResponse] = await Promise.all([
          getAdherentsByEvent(eventId),
          getAdherentsCountByEvent(eventId),
        ]);

        const safeList = Array.isArray(list) ? list : [];
        const count =
          typeof countResponse === "number"
            ? countResponse
            : Number(countResponse?.count || 0);

        setRegistrantsByEvent((previous) => ({
          ...previous,
          [eventId]: safeList,
        }));

        setCountsMap((previous) => ({
          ...previous,
          [eventId]: count,
        }));
      } catch (error) {
        console.error("Erreur chargement inscrits :", error);
        toast.error("Erreur lors du chargement des inscrits.");
      } finally {
        setRegistrantsLoadingMap((previous) => ({
          ...previous,
          [eventId]: false,
        }));
      }
    },
    []
  );

  const toggleEvent = useCallback(
    async (event) => {
      if (!event?.id) return;

      const isExpanded = expandedIds.has(event.id);

      setExpandedIds((previous) => {
        const next = new Set(previous);

        if (next.has(event.id)) {
          next.delete(event.id);
        } else {
          next.add(event.id);
        }

        return next;
      });

      if (!isExpanded && !registrantsByEvent[event.id]) {
        await refreshEventRegistrants(event.id);
      }
    },
    [expandedIds, registrantsByEvent, refreshEventRegistrants]
  );

  const openRegisterDrawer = useCallback(
    async (event) => {
      if (!event?.id) return;

      let registered = registrantsByEvent[event.id];

      if (!registered) {
        await refreshEventRegistrants(event.id);
        registered = registrantsByEvent[event.id] || [];
      }

      const registeredIds = new Set(
        (registered || []).map((adherent) => String(adherent.id))
      );

      setRegisterDrawer({
        open: true,
        event,
        registeredIds,
      });
    },
    [registrantsByEvent, refreshEventRegistrants]
  );

  const closeRegisterDrawer = useCallback(() => {
    setRegisterDrawer({
      open: false,
      event: null,
      registeredIds: new Set(),
    });
  }, []);

  const openUnregisterDrawer = useCallback(
    async (event, currentRegistrants = []) => {
      if (!event?.id) return;

      let list = currentRegistrants;

      if (!list?.length && !registrantsByEvent[event.id]) {
        await refreshEventRegistrants(event.id);
        list = registrantsByEvent[event.id] || [];
      }

      setUnregisterDrawer({
        open: true,
        event,
        adherents: list || [],
      });
    },
    [registrantsByEvent, refreshEventRegistrants]
  );

  const closeUnregisterDrawer = useCallback(() => {
    setUnregisterDrawer({
      open: false,
      event: null,
      adherents: [],
    });
  }, []);

  const openResultDrawer = useCallback(
    async (event, adherent) => {
      if (!event?.id || !adherent?.id) return;

      setResultDrawer({
        open: true,
        event,
        adherent,
        results: null,
        loading: true,
      });

      try {
        const results = await getAdherentResults(adherent.id, event.id);

        setResultDrawer({
          open: true,
          event,
          adherent,
          results: results || {},
          loading: false,
        });
      } catch (error) {
        console.error("Erreur chargement résultats :", error);
        toast.error("Erreur lors du chargement des résultats.");

        setResultDrawer({
          open: true,
          event,
          adherent,
          results: {},
          loading: false,
        });
      }
    },
    []
  );

  const closeResultDrawer = useCallback(() => {
    setResultDrawer({
      open: false,
      event: null,
      adherent: null,
      results: null,
      loading: false,
    });
  }, []);

  const stats = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);

    const total = filteredEvents.length;
    const upcoming = filteredEvents.filter((event) => !eventIsPast(event)).length;
    const past = filteredEvents.filter((event) => eventIsPast(event)).length;

    const withRegistrations = filteredEvents.filter(
      (event) => Number(countsMap[event.id] || 0) > 0
    ).length;

    const totalRegistrations = filteredEvents.reduce(
      (sum, event) => sum + Number(countsMap[event.id] || 0),
      0
    );

    return {
      total,
      upcoming,
      past,
      withRegistrations,
      totalRegistrations,
      categories: CATE_EVENTS.length,
    };
  }, [filteredEvents, countsMap]);

  return {
    events,
    filteredEvents,
    visibleEvents,
    eventTypes,
    stats,

    filters,
    setFilters,
    resetFilters,

    enseignants,
    countsMap,

    expandedIds,
    registrantsByEvent,
    registrantsLoadingMap,

    loading,
    hasMore,
    loadMore,
    refresh,

    toggleEvent,

    registerDrawer,
    openRegisterDrawer,
    closeRegisterDrawer,

    unregisterDrawer,
    openUnregisterDrawer,
    closeUnregisterDrawer,

    resultDrawer,
    openResultDrawer,
    closeResultDrawer,

    refreshEventRegistrants,
  };
}

export default useSuiviCompetitionsDashboard;